/**
 * **Tarifas › Catálogo de categorias** (W9-CATEGORY-CATALOG, D462, ADR-0080) — the Admin's view-model over the governed
 * service-category catalog (`ServiceCategoriesApi`, `/v1/config/service-categories/*`).
 *
 * - What a MARKET resolves now: each category with the scope and version that decided it, its status (and where it was
 *   switched off), its features and whether a fare (the market's or its region's) can price it.
 * - The editor: a category entry as a FORM (never a JSON blob) for one bucket — platform, country, administrative region
 *   or market — validated by the kernel's own `parseCatalogEntry` before anything is sent, proposed with a reason.
 * - Four eyes: approve / reject a pending version or reactivation is HIDDEN from its author with the reason shown (the
 *   server compares the session identities anyway: `SELF_APPROVAL`); withdraw is the author's only.
 * - Deactivate at once (no version); ask a second operator to reactivate. Copy an entry (and its fare) to other buckets as
 *   pending drafts.
 *
 * Pure functions; the screen renders them. Neutral: no country, category or language is named.
 */
import { type Result } from '@vello/core';
import type {
  ApiError,
  CatalogCopyResultView,
  CatalogScopeOnWire,
  CatalogTargetOnWire,
  CatalogVersionView,
  CategoryCatalogEntryOnWire,
  CategoryStatusView,
  MarketCatalogView,
  ServiceCategoriesApi,
} from '@vello/api-client';
import { CATEGORY_FEATURES, VEHICLE_CLASSES, isCatalogTarget, parseCatalogEntry, type CatalogProblem } from '@vello/service-categories';
import { grantsAllow, type KnownGrants } from './console-access.js';

export type ServiceCategoriesRuntime = { readonly serviceCategories: ServiceCategoriesApi };

/** The languages the editor offers labels for (the Admin's own four; any other tag an entry carries is kept). */
export const CATALOG_EDITOR_LANGUAGES = ['pt', 'en', 'es', 'it'] as const;
export const CATALOG_FEATURE_NAMES: readonly string[] = CATEGORY_FEATURES;
export const CATALOG_VEHICLE_CLASSES: readonly string[] = VEHICLE_CLASSES;

// ---- authority (display only — the server re-authorizes every call) --------------------------------------------------

export function serviceCategoryAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayPropose: boolean; readonly mayApprove: boolean } {
  if (grants === undefined) return { mayView: true, mayPropose: true, mayApprove: true };
  return {
    mayView: grantsAllow(grants, { permission: 'service_category:view', target: 'GEO' }),
    mayPropose: grantsAllow(grants, { permission: 'service_category:propose', target: 'GEO' }),
    mayApprove: grantsAllow(grants, { permission: 'service_category:approve', target: 'GEO' }),
  };
}

const sameId = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();

export type CatalogApproveBlock = 'OWN_PROPOSAL' | 'NO_AUTHORITY' | 'NOT_PENDING';

/** Why Approve is hidden on a pending version or reactivation, or undefined when it may be shown. */
export function catalogApproveBlock(author: string, pending: boolean, operatorId: string | undefined, mayApprove: boolean, selfApproval = false): CatalogApproveBlock | undefined {
  if (!pending) return 'NOT_PENDING';
  if (!mayApprove) return 'NO_AUTHORITY';
  if (!selfApproval && operatorId !== undefined && sameId(author, operatorId)) return 'OWN_PROPOSAL';
  return undefined;
}

/** Withdraw is the author's own act. */
export function catalogWithdrawable(author: string, pending: boolean, operatorId: string | undefined, mayPropose: boolean): boolean {
  return pending && mayPropose && operatorId !== undefined && sameId(author, operatorId);
}

/** Reject: anyone with the checker word except the author (who withdraws instead). */
export function catalogRejectable(author: string, pending: boolean, operatorId: string | undefined, mayApprove: boolean): boolean {
  return pending && mayApprove && !(operatorId !== undefined && sameId(author, operatorId));
}

/** Pending catalog versions first by bucket then code. */
export function pendingCatalogVersions(versions: readonly CatalogVersionView[]): readonly CatalogVersionView[] {
  return versions.filter((v) => v.state === 'PENDING');
}

/** Status rows with a reactivation waiting for a second operator. */
export function pendingReactivations(status: readonly CategoryStatusView[]): readonly CategoryStatusView[] {
  return status.filter((s) => s.pendingReactivation !== undefined);
}

/** The latest APPROVED version of a bucket's code (the base an edit starts from), or undefined. */
export function latestApproved(versions: readonly CatalogVersionView[], target: CatalogTargetOnWire, code: string): CatalogVersionView | undefined {
  return versions
    .filter((v) => v.state === 'APPROVED' && v.scope === target.scope && v.scopeRef === target.scopeRef && v.code === code)
    .sort((a, b) => b.version - a.version)[0];
}

// ---- targets ---------------------------------------------------------------------------------------------------------

/** The buckets a market's editor offers: the market, its region (when it has one), its country and the platform. */
export function editorTargets(market: MarketCatalogView['market'] | undefined): readonly CatalogTargetOnWire[] {
  if (market === undefined) return [];
  return [
    { scope: 'market', scopeRef: market.marketId },
    ...(market.regionCode !== undefined ? [{ scope: 'region' as const, scopeRef: market.regionCode }] : []),
    { scope: 'country', scopeRef: market.countryCode },
    { scope: 'platform', scopeRef: '' },
  ];
}

export const targetKey = (t: CatalogTargetOnWire): string => `${t.scope}|${t.scopeRef}`;

export function targetFromKey(key: string): CatalogTargetOnWire | undefined {
  const [scope, scopeRef = ''] = key.split('|');
  const t = { scope: scope as CatalogScopeOnWire, scopeRef };
  return isCatalogTarget(t) ? t : undefined;
}

// ---- the entry form ---------------------------------------------------------------------------------------------------

export interface CatalogEntryForm {
  readonly code: string;
  readonly kind: 'mobility' | 'delivery';
  readonly nameKey: string;
  readonly labels: Readonly<Record<string, string>>;
  readonly iconToken: string;
  readonly order: string;
  readonly message: Readonly<Record<string, string>>;
  readonly vehicleClasses: readonly string[];
  /** D705 (G7): the passengers a vehicle of the category carries; blank = not stated (the rider app shows no seats). */
  readonly seats: string;
  readonly features: Readonly<Record<string, boolean>>;
  readonly lockedFeatures: readonly string[];
}

/** A form from an entry (an edit of the version in force) or blank (a new category). */
export function catalogEntryForm(entry?: CategoryCatalogEntryOnWire): CatalogEntryForm {
  if (entry === undefined) {
    return { code: '', kind: 'mobility', nameKey: '', labels: {}, iconToken: 'car', order: '0', message: {}, vehicleClasses: ['car'], seats: '', features: {}, lockedFeatures: [] };
  }
  return {
    code: entry.code,
    kind: entry.kind,
    nameKey: entry.nameKey,
    labels: { ...entry.labels },
    iconToken: entry.iconToken,
    order: String(entry.order),
    message: { ...(entry.message?.labels ?? {}) },
    vehicleClasses: [...entry.vehicleClasses],
    seats: entry.seats !== undefined ? String(entry.seats) : '',
    features: { ...entry.features },
    lockedFeatures: [...(entry.lockedFeatures ?? [])],
  };
}

const nonBlank = (r: Readonly<Record<string, string>>): Record<string, string> => Object.fromEntries(Object.entries(r).filter(([, v]) => v.trim() !== '').map(([k, v]) => [k, v.trim()]));

/**
 * The entry a form describes for a target, validated by the KERNEL (the same `parseCatalogEntry` the server runs), or
 * every problem at once. A blank name key defaults to `category.<code>.name`.
 */
export function catalogEntryFromForm(form: CatalogEntryForm, target: CatalogTargetOnWire): Result<CategoryCatalogEntryOnWire, readonly CatalogProblem[]> {
  const order = form.order.trim() === '' ? Number.NaN : Number(form.order);
  const message = nonBlank(form.message);
  const raw = {
    code: form.code.trim(),
    kind: form.kind,
    nameKey: form.nameKey.trim() !== '' ? form.nameKey.trim() : `category.${form.code.trim()}.name`,
    labels: nonBlank(form.labels),
    iconToken: form.iconToken,
    order,
    ...(Object.keys(message).length > 0 ? { message: { labels: message } } : {}),
    vehicleClasses: form.vehicleClasses,
    ...(form.seats.trim() !== '' ? { seats: Number(form.seats.trim()) } : {}),
    features: form.features,
    ...(form.lockedFeatures.length > 0 ? { lockedFeatures: form.lockedFeatures } : {}),
  };
  const parsed = parseCatalogEntry(raw, { scope: target.scope, scopeRef: target.scopeRef });
  return parsed.ok ? { ok: true, value: parsed.entry as unknown as CategoryCatalogEntryOnWire } : { ok: false, error: parsed.problems };
}

export function toggle(list: readonly string[], item: string): readonly string[] {
  return list.includes(item) ? list.filter((x) => x !== item) : [...list, item];
}

/** A copy result line: which target, and whether the catalog (and the fare) draft was created. */
export function copyResultSummary(r: CatalogCopyResultView): { readonly target: string; readonly catalogOk: boolean; readonly fareOk?: boolean; readonly code?: string } {
  const code = r.catalog.ok ? (r.fare !== undefined && !r.fare.ok ? r.fare.reasonCode : undefined) : r.catalog.reasonCode;
  return {
    target: r.target.scopeRef === '' ? r.target.scope : r.target.scopeRef,
    catalogOk: r.catalog.ok,
    ...(r.fare !== undefined ? { fareOk: r.fare.ok } : {}),
    ...(code !== undefined ? { code } : {}),
  };
}

// ---- loaders ---------------------------------------------------------------------------------------------------------

export function loadMarketCatalog(runtime: ServiceCategoriesRuntime, marketId: string): Promise<Result<MarketCatalogView, ApiError>> {
  return runtime.serviceCategories.market(marketId);
}

export function loadCatalogChanges(runtime: ServiceCategoriesRuntime, filter: { readonly scope?: CatalogScopeOnWire; readonly scopeRef?: string; readonly code?: string } = {}) {
  return runtime.serviceCategories.list(filter);
}
